import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * CSS custom-property name set on the host element.
 * The SCSS for `.panel-floating` references this via `var(--toolbar-anchor-top)`.
 */
const cssVarAnchorTop = '--toolbar-anchor-top';

type UseToolbarPositionOptions = {
  /**
   * Returns the portal host element for this toolbar instance.
   * The hook sets a CSS custom property on it for vertical alignment.
   */
  getHost: () => HTMLElement | null;
  /**
   * Returns the element the toolbar should vertically align with.
   * The hook measures this element's top offset relative to the host
   * and exposes it as `--toolbar-anchor-top`.
   *
   * When null, the toolbar falls back to `top: 0` via CSS.
   */
  getAnchor: () => HTMLElement | null;
  /**
   * When false the hook removes all observers and clears the custom
   * property so the panel falls back to its default CSS position.
   */
  enabled: boolean;
  /** Re-measure when this value changes (e.g. active card face index). */
  measurementKey?: unknown;
};

/**
 * Sets `--toolbar-anchor-top` on the toolbar's portal host so that the
 * panel aligns vertically with a caller-supplied anchor element.
 *
 * Horizontal positioning is handled entirely by CSS layout — the host
 * container is sized to the toolbar area, and the panel sits flush-right.
 */
export const useToolbarPosition = ({
  getHost,
  getAnchor,
  enabled,
  measurementKey,
}: UseToolbarPositionOptions): void => {
  const rafRef = useRef<number | null>(null);

  const measure = () => {
    const host = getHost();
    if (!host) return;

    const anchor = getAnchor();
    if (!anchor) {
      host.style.removeProperty(cssVarAnchorTop);
      return;
    }

    const hostRect = host.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const top = Math.max(Math.round(anchorRect.top - hostRect.top), 0);

    host.style.setProperty(cssVarAnchorTop, `${top}px`);
  };

  // Measure synchronously before paint so toolbar opens at correct position.
  useLayoutEffect(() => {
    if (enabled) {
      measure();
    } else {
      const host = getHost();
      if (host) {
        host.style.removeProperty(cssVarAnchorTop);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getHost/getAnchor are stable module-level functions
  }, [enabled, measurementKey]);

  // Keep the property in sync with layout changes.
  useEffect(() => {
    if (!enabled) return undefined;

    // Retry in case useLayoutEffect fired before refs were committed.
    measure();
    rafRef.current = requestAnimationFrame(measure);

    const onResize = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };

    window.addEventListener('resize', onResize);

    let resizeObserver: ResizeObserver | null = null;
    const anchor = getAnchor();

    if (anchor && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(anchor);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      resizeObserver?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getHost/getAnchor are stable module-level functions
  }, [enabled, measurementKey]);
};
